import { Dialog } from '@progress/kendo-react-dialogs';
import { SubmitButton, TextField } from 'app/components/form-fields';
import { BackActionDialogContentLayout } from 'app/components/layouts';
import useClickOutside from 'app/hooks/useClickOutside';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import './EditFleetNameDialog.less';
import { lettersNumbersAndSpecialsRegex } from 'app/common/utils';
import useFormField from 'app/hooks/form-fields/useFormField';
import useInputPatternValidation from 'app/hooks/form-fields/useInputPatternValidation';
import { InputChangeEvent } from '@progress/kendo-react-inputs';

interface Props {
  initialValue: string;
  isLoading?: boolean;
  onChange: (value: string) => void;
  onClose: () => void;
}

export default function EditFleetNameDialog({
  initialValue,
  isLoading,
  onChange,
  onClose,
}: Props) {
  const { t } = useTranslation();

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(onClose);

  const { value, setValue, error, setError } = useFormField(initialValue);
  const { isPatternMatch, onKeyDown } = useInputPatternValidation(
    lettersNumbersAndSpecialsRegex,
  );

  function handleUpdate() {
    if (!validateName()) {
      return;
    }

    onChange(value!);
  }

  function validateName() {
    if (!value?.trim()) {
      setError(t('access_sharing.dialogs.edit_fleet_name.field_is_required'));
      return false;
    }

    return true;
  }

  const handleChange = (e: InputChangeEvent) => {
    const inputValue = e.target.value as string;

    if (isPatternMatch(inputValue)) {
      setValue(inputValue);
      setError('');
    }
  };

  return (
    <Dialog className="modal-container">
      <BackActionDialogContentLayout
        className="edit-fleet-name-dialog"
        onBack={onClose}
      >
        <h2 className="edit-fleet-name-dialog__title">
          {t('access_sharing.dialogs.edit_fleet_name.label')}
        </h2>

        <TextField
          label={t('access_sharing.dialogs.edit_fleet_name.label')}
          required
          value={value}
          placeholder={t('access_sharing.dialogs.edit_fleet_name.placeholder')}
          maxLength={32}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          disabled={isLoading}
          error={error}
        />

        <SubmitButton disabled={isLoading} onClick={handleUpdate}>
          {t('save')}
        </SubmitButton>
      </BackActionDialogContentLayout>
    </Dialog>
  );
}
